import React from 'react';
import CluePreview from './CluePreview';
import ClueField from './ClueField';
import { ModifierBar, ToolButton } from './CluesPanel';

// Keeps focus in the clue field (and the phone keyboard up) while stepping.
const keepFocus = e => e.preventDefault();

// Narrow screens: one clue field right under the grid, with arrows through all clues
// (across, then down), so the grid stays in view while writing. The suggestions open
// below it (see WordDesk placement="below").
const ClueStepper = React.forwardRef(({
  words, word, clue, suggestOpen, inputRef,
  onStep, onChange, onFocus, onBlur, onRequestSuggest,
  onOpenEmoji, onInsertSoftHyphen, onAutoHyphenate,
}, ref) => {
  if (!word) return null;
  const index = words.findIndex(w => w.id === word.id);

  const focusEnd = () => setTimeout(() => {
    const input = inputRef.current;
    if (!input) return;
    input.focus();
    input.setSelectionRange(input.value.length, input.value.length);
  }, 0);

  return (
    <div className={`clue-stepper ${clue.trim() ? '' : 'missing'}`} ref={ref}>
      <button type="button" className="stepper-arrow" onMouseDown={keepFocus} onClick={() => onStep(-1)}
        aria-label="Previous clue" title="Previous clue">
        <span className="material-symbols-outlined" aria-hidden="true">chevron_left</span>
      </button>

      <div className="stepper-body">
        <div className="word-header">
          <span className="word-label">
            <span className="stepper-pos">
              {word.direction === 'across' ? '→' : '↓'} {index + 1}/{words.length}
            </span>
            {word.answer}
          </span>
          <div className="word-tools">
            <ToolButton icon="add_reaction" label="Insert emoji at cursor" onClick={() => onOpenEmoji(word.id, inputRef.current)} />
            <ToolButton icon="remove" label="Insert soft hyphen at cursor" onClick={() => onInsertSoftHyphen(word.id, inputRef.current)} />
            <ToolButton icon="auto_fix_high" label="Auto-place soft hyphens" onClick={() => onAutoHyphenate(word.id)} />
          </div>
        </div>
        <div className="clue-input-container">
          <CluePreview clue={clue} />
          <ClueField
            ref={inputRef}
            suggestOpen={suggestOpen}
            onRequestSuggest={onRequestSuggest}
            value={clue}
            onChange={e => onChange(e.target.value)}
            onFocus={onFocus}
            onBlur={onBlur}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); onStep(e.shiftKey ? -1 : 1); } }}
            className="clue-input"
            aria-label={`Clue for ${word.answer}`}
          />
        </div>
        <ModifierBar clue={clue} onChange={onChange} onFocusEnd={focusEnd} />
      </div>

      <button type="button" className="stepper-arrow" onMouseDown={keepFocus} onClick={() => onStep(1)}
        aria-label="Next clue" title="Next clue">
        <span className="material-symbols-outlined" aria-hidden="true">chevron_right</span>
      </button>
    </div>
  );
});

export default ClueStepper;
